import { useState } from "react";
import {
  IPhoneCustomer, PHONE_MAX_NAME, PHONE_MAX_PHONE_INPUT,
} from "../models/IPhoneOrder";
import {
  createPhoneCustomer, existingCustomerIdFromError, phoneErrorCode,
  phoneErrorMessage, updatePhoneCustomer,
} from "../services/phoneOrderService";
import PhoneAddressFields, { IPhoneAddressValue } from "./PhoneAddressFields";

const SAVE_ERROR: Record<string, string> = {
  InvalidPhoneNumber: "Telefon numarası geçerli değil. 0533 123 45 67 gibi yazın.",
  CityNotFound: "Seçilen ilçe bulunamadı, listeyi yenileyin.",
  TownNotFound: "Seçilen bölge bulunamadı, listeyi yenileyin.",
  TownNotInCity: "Seçilen bölge bu ilçeye ait değil. İlçeyi değiştirip bölgeyi yeniden seçin.",
  PhoneCustomerNotFound: "Bu kayıt artık yok. Aramayı yenileyin.",
  RestourantAccessDenied: "Bu restoranda işlem yapma yetkiniz yok.",
};

interface Props {
  restaurantId: string;
  /** Doluysa düzenleme, boşsa yeni kayıt. */
  initial?: IPhoneCustomer | null;
  /** Aramadan gelen metin yeni kayıtta ad ya da telefon alanına ön-doldurulur. */
  prefill?: string;
  onClose: () => void;
  onSaved: (customer: IPhoneCustomer) => void;
}

export default function PhoneCustomerFormModal({
  restaurantId, initial, prefill, onClose, onSaved,
}: Props) {
  const isEdit = !!initial;
  const prefillIsPhone = !!prefill && /^[\d+]/.test(prefill);

  const [firstName, setFirstName] = useState(initial?.firstName ?? (prefillIsPhone ? "" : prefill ?? ""));
  const [lastName, setLastName] = useState(initial?.lastName ?? "");
  const [phoneNumber, setPhoneNumber] = useState(initial?.phoneNumber ?? (prefillIsPhone ? prefill ?? "" : ""));
  const [address, setAddress] = useState<IPhoneAddressValue>({
    cityId: initial?.cityId ?? "",
    townId: initial?.townId ?? "",
    streetName: initial?.streetName ?? "",
    addressDetail: initial?.addressDetail ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [duplicateId, setDuplicateId] = useState("");

  const patchAddress = (patch: Partial<IPhoneAddressValue>) => setAddress((p) => ({ ...p, ...patch }));

  const handleSubmit = () => {
    if (!firstName.trim()) { setError("Ad zorunludur."); return; }
    if (!lastName.trim()) { setError("Soyad zorunludur."); return; }
    if (!phoneNumber.trim()) { setError("Telefon zorunludur."); return; }
    if (!address.cityId) { setError("İlçe seçilmelidir."); return; }
    if (!address.townId) { setError("Bölge seçilmelidir."); return; }
    if (!address.streetName.trim()) { setError("Sokak / cadde zorunludur."); return; }
    if (!address.addressDetail.trim()) { setError("Adres detayı zorunludur. Yarım adres teslimat yapmaz."); return; }

    setSaving(true);
    setError("");
    setDuplicateId("");

    const body = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      phoneNumber: phoneNumber.trim(),
      cityId: address.cityId,
      townId: address.townId,
      streetName: address.streetName.trim(),
      addressDetail: address.addressDetail.trim(),
    };

    // Güncelleme bir değiştirmedir: her alan gönderilir. Gövdede restourantId yok.
    const call = isEdit
      ? updatePhoneCustomer({ id: initial!.id, ...body })
      : createPhoneCustomer({ restourantId: restaurantId, ...body });

    call
      .then((res) => { onSaved(res.data); onClose(); })
      .catch((err) => {
        if (phoneErrorCode(err) === "PhoneCustomerAlreadyExists") {
          setDuplicateId(existingCustomerIdFromError(err) ?? "");
          setError("Bu telefon numarası bu restoranda zaten kayıtlı. Arama kutusunda numarayı yazıp mevcut kaydı açın.");
          return;
        }
        setError(phoneErrorMessage(err, SAVE_ERROR, "Müşteri kaydedilemedi."));
      })
      .finally(() => setSaving(false));
  };

  return (
    <div className="dinein-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <div className="dinein-modal dinein-modal--narrow">
        <div className="dinein-modal__head">
          <div>
            <div className="dinein-modal__title">
              <i className={`bi ${isEdit ? "bi-pencil-square" : "bi-person-plus-fill"} me-2`}></i>
              {isEdit ? "Müşteriyi Düzenle" : "Yeni Müşteri"}
            </div>
            <div className="dinein-modal__sub">
              {isEdit
                ? "Geçmiş siparişler bu düzeltmeden etkilenmez."
                : "Bu dizin restorana özeldir; mutfacik.com hesaplarından ayrıdır."}
            </div>
          </div>
          <button className="dinein-icon-btn" onClick={onClose} disabled={saving}><i className="bi bi-x-lg"></i></button>
        </div>

        <div className="dinein-modal__body">
          <div className="pc-form__row">
            <div className="pc-field">
              <label>Ad <span className="pc-req">*</span></label>
              <input className="form-control" value={firstName} maxLength={PHONE_MAX_NAME} disabled={saving}
                onChange={(e) => setFirstName(e.target.value)} placeholder="Ayşe" />
            </div>
            <div className="pc-field">
              <label>Soyad <span className="pc-req">*</span></label>
              <input className="form-control" value={lastName} maxLength={PHONE_MAX_NAME} disabled={saving}
                onChange={(e) => setLastName(e.target.value)} placeholder="Yılmaz" />
            </div>
          </div>

          <div className="pc-field">
            <label>Telefon <span className="pc-req">*</span></label>
            <input className="form-control" value={phoneNumber} maxLength={PHONE_MAX_PHONE_INPUT} disabled={saving}
              onChange={(e) => setPhoneNumber(e.target.value)} placeholder="0533 123 45 67" />
            <div className="dinein-small dinein-muted">
              Serbest yazabilirsiniz; kayıt +90 ile 10 hane olarak saklanır.
            </div>
          </div>

          <PhoneAddressFields value={address} onChange={patchAddress} disabled={saving} />

          {error && (
            <div className="dinein-alert dinein-alert--error">
              <i className="bi bi-exclamation-circle-fill"></i>
              <span>
                {error}
                {duplicateId && <><br /><code className="dinein-small">{duplicateId}</code></>}
              </span>
            </div>
          )}
        </div>

        <div className="dinein-modal__foot">
          <button className="dinein-btn dinein-btn--ghost" onClick={onClose} disabled={saving}>Vazgeç</button>
          <button className="dinein-btn dinein-btn--primary" onClick={handleSubmit} disabled={saving}>
            {saving
              ? <><span className="spinner-border spinner-border-sm me-1" />Kaydediliyor...</>
              : <><i className="bi bi-check-lg me-1"></i>{isEdit ? "Değişiklikleri Kaydet" : "Müşteriyi Kaydet"}</>}
          </button>
        </div>
      </div>
    </div>
  );
}
